import { Box, ButtonBase } from "@mui/material";
import React from "react";
import EditablePlaylistName from "./EditablePlaylistName";
import UserAvatar from "../general/UserAvatar";
import PlaylistCover from "../general/PlaylistCover";
import Playlist from "../../models/Playlist";
import { apiPost } from "../../services/api";
import { fetchPlaylistDetails, detailRowToTrack } from "../../services/playlist";
import { colors } from "../../theme";

interface playlistRow {
    index?: number;
    playlist: Playlist;
    ownerId?: number;
    setPlaylistDetails: (details: any) => void;
}

async function onNameChangeSave(newName: string, playlistId: number) {
    try {
        const data = await apiPost(`/v1/playlist/updatePlaylistName?playlistId=${playlistId}`, { playlistName: newName });
        console.log('Playlist name updated:', data);
    } catch (error) {
        console.error('Error updating playlist name:', error);
    }
}

export default function PlaylistRow(props: playlistRow) {
    const { index, playlist, setPlaylistDetails } = props;

    // The viewer's relationship to the playlist travels with it - the open playlist
    // decides from these whether to offer contributor management, a request button, or
    // nothing at all, and it has no other source for them.
    const relationship = {
        owned: playlist.owned,
        following: playlist.following,
        contributor: playlist.contributor,
        contributorRequested: playlist.contributorRequested,
        isPublic: playlist.isPublic,
    };

    const handleSelect = async () => {
        try {
            const tracks = await fetchPlaylistDetails(playlist.id);
            const safeTracks = Array.isArray(tracks) ? tracks : [];
            const formattedTracks = safeTracks.map(detailRowToTrack);
            setPlaylistDetails({
                playlistName: playlist.playlistName,
                playlistId: playlist.id,
                imageUrl: playlist.imageUrl,
                tracks: formattedTracks,
                ...relationship,
            });
        } catch (error) {
            // Open the playlist with no tracks rather than doing nothing, so a failed
            // details fetch doesn't leave the click looking like it did nothing at all.
            console.error("Error fetching playlist details:", error);
            setPlaylistDetails({
                playlistName: playlist.playlistName,
                playlistId: playlist.id,
                imageUrl: playlist.imageUrl,
                tracks: [],
                ...relationship,
            });
        }
    };

    return (
        <ButtonBase
            key={index}
            onClick={handleSelect}
            sx={{
                width: "100%",
                justifyContent: "flex-start",
                gap: 1.5,
                p: 1,
                borderRadius: 1.5,
                textAlign: "left",
                transition: "background-color 120ms ease",
                "&:hover": { backgroundColor: colors.elevatedHover },
            }}
        >
            <PlaylistCover images={playlist.imageUrl ? [playlist.imageUrl] : []} size={48} borderRadius={1} />
            <Box sx={{ minWidth: 0 }}>
                <EditablePlaylistName
                    name={playlist.playlistName}
                    onSave={(newName: string) => onNameChangeSave(newName, playlist.id)}
                    editable={playlist.owned !== false}
                />
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.75,
                        fontSize: 13,
                        color: colors.textSecondary,
                        mt: 0.25,
                        minWidth: 0,
                    }}
                >
                    {/* The sidebar mixes your own playlists with followed ones, so each
                        row names its creator. Yours say "you" rather than repeating your
                        own username down the whole list. */}
                    <UserAvatar username={playlist.ownerUsername} size={16} />
                    <Box
                        component="span"
                        sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                    >
                        {playlist.owned ? "You" : playlist.ownerUsername || "Unknown"}
                        {playlist.following ? " · Following" : ""}
                    </Box>
                </Box>
            </Box>
        </ButtonBase>
    );
}
